import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Grid, Button, Stack, IconButton, Tooltip, InputBase, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
} from '@mui/material';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useSelector } from 'react-redux';
import { selectEmployees } from '../redux/slices/employeesSlice';
import { sanitizeCode, selectActiveEntity, selectActiveEntityId, ALL_ENTITY_ID } from '../redux/slices/orgSlice';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import { useCrumbActionsSetter } from '../utils/crumbActions';
import { card, solidBtn, ghostBtn, field, StatCards, EmptyState, StatusChip, StatusToggle, ConfirmDialog, LoadAlert } from '../components/uiKit';
import http, { apiErrorMessage } from '../Api/http';
import { SkeletonRows } from '../components/uiKit';
import { GetDepartments, CreateDepartment, UpdateDepartment, DeleteDepartment } from '../Api/Api';

const EMPTY = { entityId: '', name: '', code: '', description: '', status: 'Active' };

const normalizeDept = (d) => ({
    id: d.id,
    name: d.name ?? '',
    code: d.code ?? '',
    description: d.description ?? '',
    status: d.isActive === false ? 'Inactive' : 'Active',
    designations: Number(d.designations) || 0,
    entityId: d.entityId,
    entityName: d.entityName ?? '',
});

export default function DepartmentsPage() {
    const employees = useSelector(selectEmployees);
    const activeEntity = useSelector(selectActiveEntity);
    const activeEntityId = useSelector(selectActiveEntityId);

    const [apiDepartments, setApiDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [q, setQ] = useState('');
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const loadDepartments = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetDepartments);
            if (body?.error) throw new Error(body.message || 'Could not load departments.');
            const list = Array.isArray(body?.data?.departments) ? body.data.departments : [];
            setApiDepartments(list.map(normalizeDept));
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load departments.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadDepartments(); }, [loadDepartments]);

    const entity = activeEntity;
    const isAll = activeEntityId === ALL_ENTITY_ID;

    const departments = useMemo(
        () => (isAll ? apiDepartments : apiDepartments.filter((d) => String(d.entityId) === String(activeEntityId))),
        [apiDepartments, activeEntityId, isAll],
    );

    const stats = useMemo(() => {
        const map = {};
        departments.forEach((d) => {
            map[d.id] = { employees: employees.filter((e) => e.department === d.name).length, designations: d.designations };
        });
        return map;
    }, [departments, employees]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return departments;
        return departments.filter((d) => [d.name, d.code, d.description].some((v) => String(v || '').toLowerCase().includes(s)));
    }, [departments, q]);

    const mapped = useMemo(() => employees.filter((e) => departments.some((d) => d.name === e.department)).length, [employees, departments]);
    const totalDesignations = useMemo(() => departments.reduce((n, d) => n + d.designations, 0), [departments]);

    const KPIS = [
        { label: 'Departments', value: departments.length, sub: entity ? `In ${entity.name}` : '—', icon: AccountTreeRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
        { label: 'Active', value: departments.filter((d) => d.status === 'Active').length, sub: 'Open for assignment', icon: ApartmentRoundedIcon, color: '#16A34A', bg: '#DCFCE7' },
        { label: 'Designations', value: totalDesignations, sub: 'Mapped under these', icon: WorkspacePremiumRoundedIcon, color: '#F59E0B', bg: '#FFF7ED' },
        { label: 'Employees Mapped', value: mapped, sub: 'Assigned to a department', icon: GroupsRoundedIcon, color: '#0EA5E9', bg: '#E0F2FE' },
    ];

    const openCreate = () => { setForm({ ...EMPTY, entityId: activeEntityId }); setTried(false); setDialog({ mode: 'create' }); };
    const openEdit = (d) => { setForm({ entityId: d.entityId, name: d.name, code: d.code, description: d.description, status: d.status }); setTried(false); setDialog({ mode: 'edit', id: d.id }); };
    const closeDialog = () => { if (!saving) setDialog(null); };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const nameTaken = apiDepartments.some((d) =>
        String(d.entityId) === String(form.entityId)
        && d.name.trim().toLowerCase() === form.name.trim().toLowerCase()
        && d.id !== dialog?.id);
    const valid = form.name.trim() && form.entityId && form.entityId !== ALL_ENTITY_ID && !nameTaken;

    const submit = async () => {
        setTried(true);
        if (!valid) {
            notify(nameTaken ? 'A department with that name already exists in that entity.'
                : (!form.entityId || form.entityId === ALL_ENTITY_ID) ? 'Choose an entity from the sidebar “Working in” switcher first.'
                    : 'Department name is required.', 'warning');
            return;
        }

        const editing = dialog.mode === 'edit';
        setSaving(true);
        try {
            const { data: body } = editing
                ? await http.put(UpdateDepartment, { id: dialog.id, name: form.name.trim(), code: sanitizeCode(form.code), description: form.description, isActive: form.status === 'Active' })
                : await http.post(CreateDepartment, { entityId: String(form.entityId), name: form.name.trim(), code: sanitizeCode(form.code), description: form.description, isActive: form.status === 'Active' });
            if (body?.error) throw new Error(body.message || 'Could not save the department.');
            notify(body?.message || (editing ? `${form.name.trim()} updated.` : `${form.name.trim()} added to ${entity?.name || 'the selected entity'}.`));
            setDialog(null);
            await loadDepartments();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the department.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        if (!confirm || deleting) return;
        setDeleting(true);
        try {
            const { data: body } = await http.delete(DeleteDepartment, { params: { id: confirm.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the department.');
            notify(body?.message || `${confirm.name} removed.`);
            setConfirm(null);
            await loadDepartments();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the department.'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    // Search, reload and New Department sit at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { loadDepartments, openCreate };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 42, width: { xs: '100%', sm: 280 } }}>
                    <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                    <InputBase placeholder="Search departments…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                </Stack>
                <Tooltip arrow title="Reload">
                    <span>
                        <IconButton onClick={() => actionsRef.current.loadDepartments()} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#64748B', height: 42, width: 42, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Button startIcon={<AddRoundedIcon />} onClick={() => actionsRef.current.openCreate()} disabled={!entity} sx={{ ...solidBtn, height: 42, px: 2.2 }}>New Department</Button>
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, q, loading, entity]);

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            <StatCards items={KPIS} />

            <Box sx={{ px: 0.5, mt: 2 }}>
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={loadDepartments} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : (
                    <Box>
                        {/* Cards share one grid and one height: as many 300px-minimum columns as fit the width. */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2.5 }}>
                            {filtered.map((d) => {
                                const st = stats[d.id] || {};
                                const accent = entity?.color || PRIMARY;
                                return (
                                    <Box
                                        key={d.id}
                                        sx={{
                                            ...card, p: 2.2, display: 'flex', flexDirection: 'column', gap: 1.8, borderRadius: '12px', borderColor: '#EAF1F6',
                                            transition: 'box-shadow .2s, border-color .2s',
                                            '&:hover': { borderColor: `${accent}66`, boxShadow: `0 10px 26px -16px ${accent}99` },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.4} sx={{ alignItems: 'flex-start' }}>
                                            <Box sx={{ width: 42, height: 42, borderRadius: '11px', bgcolor: `${accent}16`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <AccountTreeRoundedIcon sx={{ fontSize: 22, color: accent }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A', lineHeight: '22px' }} noWrap title={d.name}>{d.name}</Typography>
                                                {/* Same line on every card: code (if any) + status, so titles and stats line up across the grid. */}
                                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mt: 0.5, height: 20 }}>
                                                    {d.code && (
                                                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: accent, letterSpacing: 0.6, bgcolor: `${accent}16`, px: 0.85, py: 0.2, borderRadius: '5px', lineHeight: 1.4 }}>{d.code}</Typography>
                                                    )}
                                                    <StatusChip status={d.status} />
                                                </Stack>
                                            </Box>
                                            <Stack direction="row" spacing={0.2}>
                                                <Tooltip arrow title="Edit"><IconButton size="small" onClick={() => openEdit(d)} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                                <Tooltip arrow title="Remove"><IconButton size="small" onClick={() => setConfirm(d)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                            </Stack>
                                        </Stack>

                                        {d.description && (
                                            <Typography sx={{ fontSize: 12.5, color: '#64748B', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.description}</Typography>
                                        )}

                                        <Stack direction="row" sx={{ mt: 'auto', pt: 1.6, borderTop: '1px solid #EEF0F6' }}>
                                            {[
                                                { icon: GroupsRoundedIcon, l: 'Employees', v: st.employees ?? 0, c: '#0EA5E9' },
                                                { icon: WorkspacePremiumRoundedIcon, l: 'Designations', v: st.designations ?? 0, c: '#F59E0B' },
                                            ].map((x, i) => (
                                                <Stack key={x.l} direction="row" spacing={1} sx={{ flex: 1, alignItems: 'center', pl: i ? 2 : 0, borderLeft: i ? '1px solid #EEF0F6' : 'none' }}>
                                                    <x.icon sx={{ fontSize: 19, color: x.c }} />
                                                    <Box>
                                                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>{x.v}</Typography>
                                                        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.4 }}>{x.l}</Typography>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Box>
                        {filtered.length === 0 && (
                            <EmptyState
                                icon={AccountTreeRoundedIcon}
                                title={q ? 'No departments match that search' : 'No departments yet'}
                                hint={q ? 'Try a different name or code.' : 'Add your first department to start assigning employees.'}
                            />
                        )}
                    </Box>
                )}
            </Box>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{dialog?.mode === 'edit' ? 'Edit Department' : 'New Department'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {dialog?.mode === 'edit'
                            ? 'Update this department.'
                            : 'Added under the entity you’re working in — offered in its employee form.'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Grid container spacing={1.8}>
                        {dialog?.mode === 'create' && entity && (
                            <Grid size={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 1.3, borderRadius: '10px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY}22` }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: entity.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#fff' }}>{(entity.code || entity.name).slice(0, 3)}</Typography>
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.4 }}>Entity</Typography>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0F172A' }} noWrap>{entity.name}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 10.5, color: '#94A3B8', textAlign: 'right', maxWidth: 140, lineHeight: 1.35 }}>Switch from the sidebar to add elsewhere</Typography>
                                </Box>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField label="Department name" size="small" fullWidth value={form.name} onChange={set('name')}
                                error={tried && (!form.name.trim() || nameTaken)} helperText={nameTaken ? 'Already exists in this entity' : ' '} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Code" size="small" fullWidth value={form.code}
                                onChange={(e) => setForm((f) => ({ ...f, code: sanitizeCode(e.target.value) }))} helperText="e.g. ENG" sx={field} />
                        </Grid>
                        <Grid size={12}>
                            <TextField label="Description (optional)" size="small" fullWidth multiline minRows={2} value={form.description} onChange={set('description')} sx={field} />
                        </Grid>
                        <Grid size={12}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.8 }}>Status</Typography>
                            <StatusToggle
                                value={form.status}
                                onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                                activeHint="Offered when assigning employees"
                                inactiveHint="Kept on record, but no longer offered"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.mode === 'edit' ? 'Save Changes' : 'Add Department'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => { if (!deleting) setConfirm(null); }}
                onConfirm={doDelete}
                title="Remove department?"
                body={confirm ? `${confirm.name} will be removed. Its ${stats[confirm.id]?.designations ?? 0} designation(s) and ${stats[confirm.id]?.employees ?? 0} employee mapping(s) are handled by the server.` : ''}
                confirmLabel="Remove"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
